// NEW: UI_REVAMP
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteTicketApi, getTicketIdApi, postRebetApi } from '../../api/query/betApi';
import { getCreditRemainApi } from '../../api/query/profileApi';
import { useCheckingStore } from '../../hooks/useStore';
import Time from '../../utils/Time';
import GlassModal from '../modal/GlassModal';
import VoidActionButton from './void/VoidActionButton';
import { getVoidActionEnabled } from './void/voidButtonUtils';

const VoidButton = () => {
	const navigate = useNavigate();
	const { serverTime } = Time();
	const setCheckDel = useCheckingStore((state) => state.setCheckDel);

	const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
	const [isVoidResultModalOpen, setIsVoidResultModalOpen] = useState(false);
	const [isRebetModalOpen, setIsRebetModalOpen] = useState(false);
	const [isRebetResultModalOpen, setIsRebetResultModalOpen] = useState(false);
	const [voidMessage, setVoidMessage] = useState('');
	const [rebetMessage, setRebetMessage] = useState('');

	const accessTicketId = sessionStorage.getItem('accessTicketId');

	const getTicketId = useQuery([`getTicketIdApi`, accessTicketId], () =>
		getTicketIdApi(accessTicketId),
	);
	const getCreditRemain = useQuery(['getCreditRemainApi'], getCreditRemainApi);

	const ticket = getTicketId.data?.[0];
	const isVoidActionEnabled = useMemo(
		() => getVoidActionEnabled(ticket, serverTime),
		[ticket, serverTime],
	);
	const shouldKeepModalsMounted =
		isVoidModalOpen || isVoidResultModalOpen || isRebetModalOpen || isRebetResultModalOpen;

	const deleteTicket = useMutation({
		mutationFn: (ticketId) => deleteTicketApi(ticketId),
		onSuccess: (data) => {
			setCheckDel(false);
			setIsVoidModalOpen(false);
			setIsVoidResultModalOpen(true);
			setVoidMessage(data.message);
			getCreditRemain.refetch();
		},
		onError: (err) => {
			setCheckDel(false);
			setIsVoidModalOpen(false);
			setIsVoidResultModalOpen(true);
			setVoidMessage(err.response?.data?.message || 'Void failed.Please try again later.');
		},
	});

	const postRebet = useMutation({
		mutationFn: (ticketId) => postRebetApi(ticketId),
		onSuccess: (response) => {
			setIsRebetModalOpen(false);
			setIsRebetResultModalOpen(true);
			setRebetMessage(response.message);
			getCreditRemain.refetch();
		},
		onError: (err) => {
			setRebetMessage(err.response?.data?.message || 'Please try again later.');
			setIsRebetModalOpen(false);
			setIsRebetResultModalOpen(true);
		},
	});

	const handleCloseToHistory = () => {
		setIsVoidResultModalOpen(false);
		setIsRebetResultModalOpen(false);
		navigate('/history');
	};

	const handleConfirmVoid = () => {
		setCheckDel(true);
		deleteTicket.mutate(accessTicketId);
	};

	const handleOpenRebetConfirm = () => {
		setIsVoidResultModalOpen(false);
		setIsRebetModalOpen(true);
	};

	const handleCloseRebetConfirm = () => {
		setIsRebetModalOpen(false);
		setIsVoidResultModalOpen(true);
	};

	const handleConfirmRebet = () => {
		if (postRebet.isLoading) return;

		postRebet.mutate({
			ticketId: Number(accessTicketId),
		});
	};

	useEffect(() => {
		if (!isVoidActionEnabled) {
			setIsVoidModalOpen(false);
		}
	}, [isVoidActionEnabled]);

	if (getTicketId.isLoading) return null;
	if (!isVoidActionEnabled && !shouldKeepModalsMounted) return null;

	return (
		<>
			{isVoidActionEnabled ? (
				<VoidActionButton
					isEnabled={isVoidActionEnabled}
					onOpen={() => {
						setIsVoidModalOpen(true);
					}}
				/>
			) : null}

			{isVoidModalOpen ? (
				<GlassModal
					variant='question'
					title='Confirm Void?'
					desc='Action can’t be edit after void. Are you confirm to void?'
					isOpen={isVoidModalOpen}
					onClose={() => setIsVoidModalOpen(false)}
					isLoading={deleteTicket.isLoading}
					showFooter={!deleteTicket.isLoading}
					closeOnOverlayClick={false}
					leftButton={{ text: 'Back', onClick: () => setIsVoidModalOpen(false) }}
					rightButton={{ text: 'Confirm', onClick: handleConfirmVoid }}
				/>
			) : null}

			{isVoidResultModalOpen ? (
				deleteTicket.isError ? (
					<GlassModal
						variant='wrong'
						title='Void Failed'
						desc={voidMessage || 'Void failed.Please try again later.'}
						isOpen={isVoidResultModalOpen}
						onClose={() => setIsVoidResultModalOpen(false)}
						closeOnOverlayClick={false}
						leftButton={{ text: 'Back', onClick: () => setIsVoidResultModalOpen(false) }}
						rightButton={{ text: 'Confirm', onClick: handleCloseToHistory }}
					/>
				) : (
					<GlassModal
						variant='success'
						title='Ticket Voided.'
						desc=''
						isOpen={isVoidResultModalOpen}
						onClose={() => setIsVoidResultModalOpen(false)}
						closeOnOverlayClick={false}
						leftButton={{ text: 'Rebet', onClick: handleOpenRebetConfirm }}
						rightButton={{ text: 'Confirm', onClick: handleCloseToHistory }}
					/>
				)
			) : null}

			{isRebetModalOpen ? (
				<GlassModal
					variant='question'
					title='Confirm Rebet?'
					desc=''
					isOpen={isRebetModalOpen}
					onClose={handleCloseRebetConfirm}
					isLoading={postRebet.isLoading}
					showFooter={!postRebet.isLoading}
					closeOnOverlayClick={false}
					leftButton={{ text: 'Back', onClick: handleCloseRebetConfirm }}
					rightButton={{ text: 'Confirm', onClick: handleConfirmRebet }}
				/>
			) : null}

			{isRebetResultModalOpen ? (
				postRebet.isError ? (
					<GlassModal
						variant='wrong'
						title='Rebet Failed'
						desc={rebetMessage || 'Please try again later.'}
						isOpen={isRebetResultModalOpen}
						onClose={() => setIsRebetResultModalOpen(false)}
						closeOnOverlayClick={false}
						leftButton={{ text: 'Back', onClick: () => setIsRebetResultModalOpen(false) }}
						rightButton={{ text: 'Confirm', onClick: handleCloseToHistory }}
					/>
				) : (
					<GlassModal
						variant='success'
						title='Rebet Successfully!'
						desc=''
						isOpen={isRebetResultModalOpen}
						onClose={() => setIsRebetResultModalOpen(false)}
						closeOnOverlayClick={false}
						leftButton={false}
						rightButton={{ text: 'Confirm', onClick: handleCloseToHistory }}
					/>
				)
			) : null}
		</>
	);
};

export default VoidButton;
